import { FeatureCollection } from "geojson";
import {
  CustomMouseEvent,
  defaultStrokeColor,
  mapActive,
} from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { RootStore } from "../../shared/stores/RootStore";
import { GeoJsonMapillaryProps, MapillarySequence } from "../models/Mapillary";
import { MapMode } from "../../survey/stores/MapStore";

export class MapillaryMapSource {
  map: mapboxgl.Map;
  rootStore: RootStore;
  sequenceSelectionListenerRemovers: (() => void)[] = [];
  pointSelectionListenerRemovers: (() => void)[] = [];
  hoverListenerRemovers: (() => void)[] = [];

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;
    this.config();
  }

  onModeChange(mode: MapMode) {
    if (mode !== MapMode.MapillaryImport) {
      // this.setMapData([]);
    }

    this.disableSequenceSelectionListeners();
    this.disablePointSelectionListeners();
    this.disableHoverListeners();

    if (mode === MapMode.MapillaryImport) {
      this.enableSequenceSelection();
      this.enablePointSelection();
    }
  }

  clear() {
    if (!mapActive(this.map)) return;

    this.disableSequenceSelectionListeners();
    this.disablePointSelectionListeners();
    this.disableHoverListeners();

    if (this.map.getSource(sourceName)) {
      this.map.removeLayer(strokeLayerConfig.id);
      this.map.removeLayer(pointMultiSelectedLayerConfig.id);
      this.map.removeLayer(pointStrokeLayerConfig.id);
      this.map.removeLayer(lineLayerConfig.id);
      this.map.removeLayer(pointLayerConfig.id);
      this.map.removeLayer(directionLayerConfig.id);
      this.map.removeSource(sourceName);
    }
  }

  disableSequenceSelectionListeners() {
    this.sequenceSelectionListenerRemovers.forEach((fn) => fn());
    this.sequenceSelectionListenerRemovers = [];
  }

  disablePointSelectionListeners() {
    this.pointSelectionListenerRemovers.forEach((fn) => fn());
    this.pointSelectionListenerRemovers = [];
  }

  disableHoverListeners() {
    this.hoverListenerRemovers.forEach((fn) => fn());
    this.hoverListenerRemovers = [];
  }

  private config() {
    if (!mapActive(this.map)) return;
    if (this.map.getSource(sourceName)) return;

    this.map.addSource(sourceName, sourceConfig);
    this.map.addLayer(strokeLayerConfig);
    this.map.addLayer(lineLayerConfig);
    this.map.addLayer(directionLayerConfig);
    this.map.addLayer(pointMultiSelectedLayerConfig);
    this.map.addLayer(pointStrokeLayerConfig);
    this.map.addLayer(pointLayerConfig);
  }

  update(sequences: MapillarySequence[]) {
    if (!mapActive(this.map)) return;

    const source = this.map.getSource(sourceName) as
      | mapboxgl.GeoJSONSource
      | undefined;

    if (!source) {
      console.warn("Mapillary source not found");
      return;
    }

    source.setData(buildGeoJson(sequences, this.rootStore));
  }

  configCursorOnHover() {
    this.disableHoverListeners();

    const canvas = this.map.getCanvasContainer();

    const onSequenceEnter = () => {
      canvas.style.cursor = "pointer";
    };

    const onSequenceLeave = () => {
      canvas.style.cursor = "";
    };

    this.map.on("mouseenter", strokeLayerConfig.id, onSequenceEnter);
    this.map.on("mouseleave", strokeLayerConfig.id, onSequenceLeave);

    this.hoverListenerRemovers.push(() => {
      this.map.off("mouseenter", strokeLayerConfig.id, onSequenceEnter);
      this.map.off("mouseleave", strokeLayerConfig.id, onSequenceLeave);
    });
  }

  enableSequenceSelection() {
    this.configCursorOnHover();
    this.disableSequenceSelectionListeners();

    const { mapillaryStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const handleSelectSequence = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";

      const feature = e.features?.[0];
      const props = feature?.properties as GeoJsonMapillaryProps | undefined;
      const geometry = feature?.geometry;

      if (!props || geometry?.type !== "LineString") return;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";
        mapillaryStore.selectSequence(props.sequenceId);
      });
    };

    this.map.on("mousedown", strokeLayerConfig.id, handleSelectSequence);
    // this.map.on("mousedown", lineLayerName, handleSelectSequence);

    this.sequenceSelectionListenerRemovers.push(() => {
      this.map.off("mousedown", strokeLayerConfig.id, handleSelectSequence);
      // this.map.off("mousedown", lineLayerName, handleSelectSequence);
    });
  }

  enablePointSelection() {
    this.disablePointSelectionListeners();

    const { mapillaryStore } = this.rootStore;

    const handleSelectPoint = (e: CustomMouseEvent) => {
      const feature = e.features?.[0];
      const props = feature?.properties as GeoJsonMapillaryProps | undefined;
      const geometry = feature?.geometry;
      const isMultiSelect = !!e.originalEvent.shiftKey;

      if (!isMultiSelect) return;
      if (!props || geometry?.type !== "Point") return;

      e.preventDefault();

      mapillaryStore.selectSequence(props.sequenceId);
      mapillaryStore.togglePointSelected(props.id);
    };

    this.map.on("mousedown", pointStrokeLayerConfig.id, handleSelectPoint);

    this.pointSelectionListenerRemovers.push(() => {
      this.map.off("mousedown", pointStrokeLayerConfig.id, handleSelectPoint);
    });
  }
}

/**
 * BUILDER
 */

function buildGeoJson(
  sequences: MapillarySequence[],
  rootStore: RootStore,
): FeatureCollection {
  const { mapillaryStore } = rootStore;
  const features: GeoJSON.Feature[] = [];

  for (const sequence of sequences) {
    const sequenceSelected =
      sequence.sequenceId === mapillaryStore.selectedSequenceId;

    const keptPoints = sequence.points.filter(
      (point) => !mapillaryStore.removedPointIds.has(point.imageId),
    );

    if (keptPoints.length > 1) {
      features.push({
        type: "Feature",
        id: sequence.sequenceId,
        properties: {
          id: sequence.sequenceId,
          sequenceId: sequence.sequenceId,
          sequenceSelected,
          pointSelected: false,
          pointMultiSelected: false,
          removed: false,
        } as GeoJsonMapillaryProps,
        geometry: {
          type: "LineString",
          coordinates: keptPoints.map((p) => [p.lng, p.lat]),
        },
      });
    }

    for (const point of keptPoints) {
      const pointMultiSelected = mapillaryStore.selectedPointIds.has(
        point.imageId,
      );

      features.push({
        type: "Feature",
        id: point.imageId,
        properties: {
          id: point.imageId,
          sequenceId: sequence.sequenceId,
          sequenceSelected,
          pointSelected: false,
          pointMultiSelected,
          removed: false,
        } as GeoJsonMapillaryProps,
        geometry: {
          type: "Point",
          coordinates: [point.lng, point.lat],
        },
      });
    }
  }

  return { type: "FeatureCollection", features };
}

const mapillaryGreen = "#39AF5B";
const selectedRouteLineColor = "#FFFFFF";
const multiNodeSelectedColor = "#5FD6E6";
const selectedRouteNodeColor = "#00bfff";

const isLineString = ["==", "$type", "LineString"];
const isPoint = ["==", "$type", "Point"];

const isSequenceSelected = ["boolean", ["get", "sequenceSelected"], false];

const isPointMultiSelected = ["boolean", ["get", "pointMultiSelected"], false];

/**
 * SOURCE
 */

const sourceName = CustomSource.Mapillary;

const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

/**
 * LAYERS
 */

// The stroke around the sequence lines
const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  id: `${sourceName}-stroke`,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": defaultStrokeColor,
    "line-width": ["case", isSequenceSelected, 7, 5],
  },
  filter: isLineString,
};

// The sequence lines
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: `${sourceName}-line`,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": [
      "case",
      isSequenceSelected,
      selectedRouteLineColor,
      mapillaryGreen,
    ],
    "line-width": ["case", isSequenceSelected, 5, 3],
    "line-opacity": ["case", isSequenceSelected, 1, 0.45],
  },
  filter: isLineString,
};

// Direction arrows along the sequence
const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: directionLayerName,
  source: sourceName,
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 50,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": [
      "case",
      isSequenceSelected,
      selectedRouteLineColor,
      mapillaryGreen,
    ],
    "icon-opacity": ["case", isSequenceSelected, 1, 0.45],
  },
  filter: isLineString,
};

// Halo for multi-selected points
const pointMultiSelectedLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: `${sourceName}-multi-selected-point`,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": ["case", isPointMultiSelected, 2, 0],
    "circle-color": multiNodeSelectedColor,
  },
  filter: isPoint,
};

// Stroke around points
const pointStrokeLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: `${sourceName}-point-stroke`,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": ["case", isPointMultiSelected, 9, 6],
    "circle-color": [
      "case",
      isPointMultiSelected,
      multiNodeSelectedColor,
      defaultStrokeColor,
    ],
  },
  filter: isPoint,
};

// Point centers
const pointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: `${sourceName}-point`,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": ["case", isPointMultiSelected, 5, 4],
    "circle-color": [
      "case",
      isPointMultiSelected,
      multiNodeSelectedColor,
      isSequenceSelected,
      selectedRouteNodeColor,
      mapillaryGreen,
    ],
  },
  filter: isPoint,
};

export const mapillaryPointShiftDragLayerName = pointLayerConfig.id;
